import {useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Switch} from '@/components/ui/switch'
import {HelpIcon} from '@/components/help-icon'
import {FilterMultiSelect} from '@/components/filter-multi-select'
import {
  CLASIFICACIONES,
  PROCESOS,
  SIN_AREA_TOKEN,
  useMapaCalor,
  type Matriz,
  type MapaCalorFiltros,
  type RiesgoFilaClasificacion,
  type RiesgoFilaInterpretacion,
} from '@/hooks/sgsst/riesgos/use-ipevr'
import {cn} from '@/lib/utils'

const NIVELES: RiesgoFilaInterpretacion[] = ['I', 'II', 'III', 'IV']

// Full class names, never concatenated, so the design-token gate sees every token literally.
const CELDA_TONE_CLASS: Record<RiesgoFilaInterpretacion, string> = {
  I: 'tone-red',
  II: 'tone-orange',
  III: 'tone-amber',
  IV: 'tone-lime',
}

/** Counted server-side, never over `matriz.filas`. Its filters are separate from the grid's
 * toolbar because search, nivel and cargo don't apply here. */
export function MapaCalorView({
  businessId,
  matriz,
  onVerPendientes,
}: {
  businessId: string
  matriz: Matriz
  onVerPendientes: () => void
}) {
  const {t} = useTranslation()
  const [incluirPropuestas, setIncluirPropuestas] = useState(false)
  const [proceso, setProceso] = useState<Set<string>>(new Set())
  const [areaId, setAreaId] = useState<Set<string>>(new Set())
  const [clasificacion, setClasificacion] = useState<Set<string>>(new Set())

  const filtros: MapaCalorFiltros = useMemo(
    () => ({
      incluirPropuestas,
      proceso: [...proceso],
      areaId: [...areaId],
      clasificacion: [...clasificacion] as RiesgoFilaClasificacion[],
    }),
    [incluirPropuestas, proceso, areaId, clasificacion]
  )
  // Under metodologia=otra the counts mean nothing, so they aren't even requested.
  const aplica = matriz.metodologia === 'gtc_45'
  const {data, isLoading, isError} = useMapaCalor(businessId, filtros, aplica)

  const procesoOptions = useMemo(() => PROCESOS.map((p) => ({id: p, name: p})), [])
  const areaOptions = useMemo(
    () => [{id: SIN_AREA_TOKEN, name: t('ipevr.grid.noArea')}, ...matriz.areas.map((a) => ({id: a.id, name: a.name}))],
    [matriz.areas, t]
  )
  const clasificacionOptions = useMemo(
    () => CLASIFICACIONES.map((c) => ({id: c, name: t(`ipevr.clasificacion.${c}`)})),
    [t]
  )

  const hasActiveFilters = incluirPropuestas || proceso.size > 0 || areaId.size > 0 || clasificacion.size > 0

  function clearFilters() {
    setIncluirPropuestas(false)
    setProceso(new Set())
    setAreaId(new Set())
    setClasificacion(new Set())
  }

  if (!aplica) {
    return (
      <p
        className="surface px-6 py-10 text-center text-sm text-muted-foreground"
        data-testid="ipevr-mapa-otra-metodologia">
        {t('ipevr.mapa.otraMetodologia')}
      </p>
    )
  }

  return (
    <div className="space-y-4" data-testid="ipevr-mapa-calor">
      <div className="surface px-4 py-4">
        <div className="flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-2">
            <label className="flex items-center gap-2 text-sm text-foreground" htmlFor="ipevr-mapa-incluir-propuestas">
              <Switch
                id="ipevr-mapa-incluir-propuestas"
                checked={incluirPropuestas}
                onCheckedChange={setIncluirPropuestas}
                data-testid="ipevr-mapa-incluir-propuestas"
              />
              {t('ipevr.mapa.incluirPropuestas')}
            </label>
            <HelpIcon
              label={t('common.help.iconLabelFor', {campo: t('ipevr.mapa.incluirPropuestas')})}
              testId="ipevr-mapa-incluir-propuestas-help">
              {t('ipevr.mapa.incluirPropuestasTip')}
            </HelpIcon>
          </span>
          <FilterMultiSelect
            label={t('ipevr.mapa.filtroProceso')}
            testId="ipevr-mapa-filtro-proceso"
            options={procesoOptions}
            selected={proceso}
            onChange={setProceso}
          />
          <FilterMultiSelect
            label={t('ipevr.filter.area')}
            testId="ipevr-mapa-filtro-area"
            options={areaOptions}
            selected={areaId}
            onChange={setAreaId}
          />
          <FilterMultiSelect
            label={t('ipevr.filter.clasificacion')}
            testId="ipevr-mapa-filtro-clasificacion"
            options={clasificacionOptions}
            selected={clasificacion}
            onChange={setClasificacion}
          />
          {hasActiveFilters && (
            <Button size="sm" variant="ghost" onClick={clearFilters} data-testid="ipevr-mapa-clear-filters">
              {t('ipevr.filter.clear')}
            </Button>
          )}
        </div>
      </div>

      {isLoading ? (
        <p className="surface px-6 py-10 text-center text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : isError || !data ? (
        <p className="surface px-6 py-10 text-center text-sm text-destructive" data-testid="ipevr-mapa-error">
          {t('ipevr.mapa.loadError')}
        </p>
      ) : data.total_calificadas === 0 ? (
        <p className="surface px-6 py-10 text-center text-sm text-muted-foreground" data-testid="ipevr-mapa-empty">
          {hasActiveFilters ? t('ipevr.mapa.emptyFiltered') : t('ipevr.mapa.emptyGenuine')}
        </p>
      ) : (
        <>
          <p className="text-xs text-muted-foreground">
            {t('ipevr.mapa.totalCalificadas', {count: data.total_calificadas})}
          </p>
          {/* Conteo y % como texto, nunca solo color (accesibilidad). */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="ipevr-mapa-celdas">
            {NIVELES.map((nivel) => {
              const celda = data.celdas.find((c) => c.interpretacion === nivel)
              const count = celda?.count ?? 0
              const pct = celda?.pct ?? 0
              return (
                <div
                  key={nivel}
                  className={cn(CELDA_TONE_CLASS[nivel], 'rounded-lg p-4')}
                  data-testid={`ipevr-mapa-celda-${nivel}`}>
                  <p className="font-mono text-xs font-semibold tracking-wider uppercase">{nivel}</p>
                  <p className="mt-1 font-heading text-2xl">{count}</p>
                  <p className="text-xs">{pct}%</p>
                  <p className="mt-2 text-xs">{t(`ipevr.interpretacionLegend.${nivel}`)}</p>
                </div>
              )
            })}
          </div>
        </>
      )}

      {data && data.pendientes_calificar > 0 && (
        <div
          className="notice-amber flex flex-wrap items-center justify-between gap-2 rounded-lg px-4 py-3"
          data-testid="ipevr-mapa-pendientes">
          <span className="text-sm">{t('ipevr.grid.sinCalificarContador', {count: data.pendientes_calificar})}</span>
          <Button size="sm" variant="outline" onClick={onVerPendientes} data-testid="ipevr-mapa-ver-pendientes">
            {t('ipevr.mapa.verPendientes')}
          </Button>
        </div>
      )}
    </div>
  )
}
